/**
 * Microsoft Clarity — session recording and heatmaps, DEV BOX ONLY.
 *
 * Deliberately the inverse of the Google Analytics gate in main.ts: GA fires on
 * production only, Clarity fires on finoptima.tech only. Clarity is being used
 * to watch how the app behaves while it is being built, not to measure real
 * traffic, so renants.com visitors are never recorded.
 *
 * The env comes from <meta name="app-env">, stamped into the served HTML by the
 * Express server (modules/seoMeta.js) from the backend NODE_ENV. It is the only
 * trustworthy signal: vue-app/.env is committed with VITE_NODE_ENV=production,
 * so every build claims to be production wherever it was made. See seoEnv.ts.
 *
 * When the tag is absent — the Vite dev server serving index.html itself —
 * appEnv() returns "unknown" and Clarity stays off. To record a local `vite dev`
 * session, set VITE_CLARITY_FORCE=true in a local .env (never commit it).
 */
import { appEnv } from './seoEnv'
import type { Router } from 'vue-router'

const CLARITY_ENVS = ['development']

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] }
  }
}

/** True only where Clarity should record — the dev box, or a forced local run. */
export function isClarityEnabled(): boolean {
  if (!import.meta.env.VITE_CLARITY_PROJECT_ID) return false
  if (String(import.meta.env.VITE_CLARITY_FORCE).toLowerCase() === 'true') return true
  return CLARITY_ENVS.includes(appEnv())
}

/**
 * Inject the Clarity tag and start tagging recordings with the SPA route.
 * Safe to call more than once; the second call is a no-op.
 */
export function initClarity(router?: Router): void {
  if (!isClarityEnabled() || window.clarity) return

  const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID

  // Official Clarity snippet, transcribed rather than eval'd from a string so
  // it survives a Content-Security-Policy that forbids inline script.
  window.clarity =
    window.clarity ||
    function (...args: unknown[]) {
      ;(window.clarity!.q = window.clarity!.q || []).push(args)
    }

  const tag = document.createElement('script')
  tag.async = true
  tag.src = `https://www.clarity.ms/tag/${projectId}`
  document.head.appendChild(tag)

  // Clarity follows history changes on its own, but a custom tag makes the
  // route filterable in the dashboard ("show me sessions that hit /post-ad").
  router?.afterEach((to) => {
    window.clarity?.('set', 'route', to.name ? String(to.name) : to.path)
  })
}
